import { captureEvent } from './posthog';

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const captureUserSignedUp = (p: {
  method: 'email' | 'microsoft_sso' | 'enterprise_sso' | string;
  email_domain: string;
  user_type?: string | number;
}) => captureEvent('user_signed_up', p);

export const captureUserLoggedIn = (p: {
  method: 'email' | 'microsoft_sso' | 'enterprise_sso' | string;
  email_domain: string;
}) => captureEvent('user_logged_in', p);

export const captureUserLoggedOut = () => captureEvent('user_logged_out');

export const captureUserLoginFailed = (p: {
  method: 'email' | 'microsoft_sso' | 'enterprise_sso' | string;
  email_domain?: string;
}) => captureEvent('user_login_failed', p);

export const captureSsoInitiated = (p: {
  provider: 'microsoft' | 'enterprise' | string;
  email_domain?: string;
}) => captureEvent('sso_initiated', p);

export const capturePasswordResetRequested = (p: { email_domain?: string }) =>
  captureEvent('password_reset_requested', p);

export const captureSignupInvalidEmail = (p: {
  reason: 'invalid_format' | 'personal_domain';
  domain?: string;
}) => captureEvent('signup_invalid_email', p);

// ---------------------------------------------------------------------------
// Inspections
// ---------------------------------------------------------------------------

export const captureInspectionSearched = (p: {
  query?: string;
  result_count: number;
  filter_count: number;
  has_483_filter?: boolean;
}) => captureEvent('inspection_searched', p);

export const captureInspectionDetailViewed = (p: {
  inspection_id: string | number;
  company_name?: string;
  site_country?: string;
  inspection_category?: string;
  had_483?: boolean;
}) => captureEvent('inspection_detail_viewed', p);

export const captureForm483Downloaded = (p: {
  inspection_id: string | number;
  company_name?: string;
  source: 'list' | 'detail' | 'landing';
  site_country?: string;
}) => captureEvent('form_483_downloaded', p);

export const captureEirDownloaded = (p: {
  inspection_id: string | number;
  company_name?: string;
  source: 'list' | 'detail';
}) => captureEvent('eir_downloaded', p);

export const captureInspectionTabChanged = (p: {
  inspection_id: string | number;
  tab: string;
}) => captureEvent('inspection_tab_changed', p);

export const captureInspectionNoteCreated = (p: {
  inspection_id: string | number;
  company_name?: string;
  has_attachment: boolean;
}) => captureEvent('inspection_note_created', p);

export const captureInspectionChatbotMessageSent = (p: {
  inspection_id: string | number;
}) => captureEvent('inspection_chatbot_message_sent', p);

// ---------------------------------------------------------------------------
// Observations / Search
// ---------------------------------------------------------------------------

export const captureFreetextSearchExecuted = (p: {
  query: string;
  result_count: number;
  has_filters: boolean;
}) => captureEvent('freetext_search_executed', p);

export const captureAdvancedSearchExecuted = (p: {
  category_count: number;
  keyword_count: number;
  inspector_count: number;
  result_count: number;
}) => captureEvent('advanced_search_executed', p);

export const captureObservationsCsvExported = (p: {
  search_type: 'advanced' | 'freetext' | 'qs';
  result_count?: number;
  filter_count?: number;
}) => captureEvent('observations_csv_exported', p);

export const captureSearchSaved = (p: {
  search_type: string;
  filter_count?: number;
}) => captureEvent('search_saved', p);

export const captureSavedSearchExecuted = (p: {
  search_type?: string;
  search_age_days?: number;
}) => captureEvent('saved_search_executed', p);

export const captureSavedSearchDeleted = (p: {
  search_type?: string;
  search_age_days?: number;
}) => captureEvent('saved_search_deleted', p);

export const captureSearchNoResults = (p: {
  query: string;
  query_length: number;
  search_type: string;
  source_page: string;
  filter_count?: number;
  filters_applied?: Record<string, unknown>;
}) => {
  // Guard: only fire for queries longer than 4 characters.
  if (p.query_length <= 4) return;
  captureEvent('search_no_results', p);
};

export const captureAiAgentQuerySubmitted = (p: {
  query_length: number;
  pipeline_steps?: number;
}) => captureEvent('ai_agent_query_submitted', p);

// ---------------------------------------------------------------------------
// Enforcement
// ---------------------------------------------------------------------------

export const captureWarningLetterViewed = (p: {
  warning_letter_id: string | number;
  company_name?: string;
  issue_year?: number;
  source: 'list' | 'detail';
}) => captureEvent('warning_letter_viewed', p);

export const captureImportAlertViewed = (p: {
  alert_id?: string | number;
  company_name?: string;
}) => captureEvent('import_alert_viewed', p);

export const captureRecallViewed = (p: {
  recall_id?: string | number;
  company_name?: string;
  recall_class?: string;
}) => captureEvent('recall_viewed', p);

// ---------------------------------------------------------------------------
// My Hub — Watchlist
// ---------------------------------------------------------------------------

export const captureWatchlistItemAdded = (p: {
  entity_type: 'site' | 'company' | string;
  entity_id?: string | number;
  entity_name?: string;
  entity_country?: string;
}) => captureEvent('watchlist_item_added', p);

export const captureWatchlistItemRemoved = (p: {
  entity_type: 'site' | 'company' | string;
  entity_id?: string | number;
  entity_name?: string;
}) => captureEvent('watchlist_item_removed', p);

export const captureWatchlistShared = (p: {
  recipient_email?: string | null;   // domain extracted internally — never stored full email
  watchlist_size?: number;
}) => {
  if (!p.recipient_email) return;
  const domain = p.recipient_email.includes('@')
    ? p.recipient_email.split('@')[1]
    : p.recipient_email;
  captureEvent('watchlist_shared', { recipient_email_domain: domain, watchlist_size: p.watchlist_size });
};

export const captureWatchlistTestEmailSent = (p: { watchlist_size?: number }) =>
  captureEvent('watchlist_test_email_sent', p);

export const captureNoteViewed = (p: {
  note_id: string | number;
  linked_entity_type?: string;
}) => captureEvent('note_viewed', p);

// ---------------------------------------------------------------------------
// Supply Chain
// ---------------------------------------------------------------------------

export const captureSupplyChainFileUploaded = (p: {
  file_type: string;
  row_count?: number;
}) => captureEvent('supply_chain_file_uploaded', p);

export const captureSupplyChainListShared = (p: {
  recipient_email?: string | null;
  list_size?: number;
}) => {
  if (!p.recipient_email) return;
  const domain = p.recipient_email.includes('@')
    ? p.recipient_email.split('@')[1]
    : p.recipient_email;
  captureEvent('supply_chain_list_shared', { recipient_email_domain: domain, list_size: p.list_size });
};

export const captureSupplyChainSampleDownloaded = () =>
  captureEvent('supply_chain_sample_downloaded');

// ---------------------------------------------------------------------------
// Analytics Pages
// ---------------------------------------------------------------------------

export const captureComparativeAnalysisViewed = (p?: {
  entity_count?: number;
  entity_type?: string;
}) => captureEvent('comparative_analysis_viewed', p);

export const captureGlobalAnalyticsViewed = (p?: { filters_applied?: boolean }) =>
  captureEvent('global_analytics_viewed', p);

export const captureDrugAnalyticsViewed = (p: {
  drug_id?: string | number;
  drug_name?: string;
}) => captureEvent('drug_analytics_viewed', p);

export const captureSiteIntelligenceViewed = (p?: { filters_applied?: boolean }) =>
  captureEvent('site_intelligence_viewed', p);

// ---------------------------------------------------------------------------
// Sites, Companies, Investigators
// ---------------------------------------------------------------------------

export const captureSiteDetailViewed = (p: {
  site_id: string | number;
  fei_number?: string;
  company_name?: string;
  country?: string;
}) => captureEvent('site_detail_viewed', p);

export const captureCompanyProfileViewed = (p: {
  company_id: string | number;
  company_name?: string;
}) => captureEvent('company_profile_viewed', p);

export const captureOrganizationTabChanged = (p: {
  org_id: string | number;
  tab: string;
}) => captureEvent('organization_tab_changed', p);

export const captureInvestigatorDetailViewed = (p: {
  investigator_id: string | number;
  investigator_name?: string;
  total_inspections?: number;
}) => captureEvent('investigator_detail_viewed', p);

// ---------------------------------------------------------------------------
// Pricing & Conversion
// ---------------------------------------------------------------------------

export const capturePricingPageViewed = (p: {
  current_plan?: string;
  referrer_page?: string;
  is_authenticated?: boolean;
}) => captureEvent('pricing_page_viewed', p);

export const captureUpgradeClicked = (p: {
  trigger: 'header' | 'pricing_page' | 'gated_feature';
  source_page?: string;
  from_tier?: string;
}) => captureEvent('upgrade_clicked', p);

export const captureItemAddedToCart = (p: {
  item_type: '483' | 'eir' | 'plan' | string;
  item_id?: string | number;
  document_name?: string;
}) => captureEvent('item_added_to_cart', p);

export const captureCheckoutStarted = (p: {
  cart_item_count?: number;
}) => captureEvent('checkout_started', p);

// ---------------------------------------------------------------------------
// My Hub — Misc
// ---------------------------------------------------------------------------

export const captureWeeklyUpdateOpened = (p: {
  update_id?: string | number;
  update_date?: string;
}) => captureEvent('weekly_update_opened', p);

export const captureNotificationViewed = (p: {
  notification_type?: string;
  notification_age_days?: number;
}) => captureEvent('notification_viewed', p);

export const captureDailyCompanionOpened = (p: { companion_date?: string }) =>
  captureEvent('daily_companion_opened', p);

// ---------------------------------------------------------------------------
// Search — new surfaces
// Guard: query.trim().length > 4 mirrors the existing search_no_results convention.
// is_repeat_query: true when the same query is re-submitted (backspace + retype or
// double-submit). Callers track lastFiredQuery in a private class property.
// ---------------------------------------------------------------------------

export const captureMhraEuSearchExecuted = (p: {
  result_count?: number;
  filter_count?: number;
}) => captureEvent('mhra_eu_search_executed', p);

export const captureMhraDisclosureLogSearched = (p: {
  result_count?: number;
}) => captureEvent('mhra_disclosure_log_searched', p);

export const captureMedicalDeviceSearchExecuted = (p: {
  query: string;
  result_count?: number;
}) => {
  if (p.query.trim().length <= 4) return;
  captureEvent('medical_device_search_executed', p);
};

export const captureObservationsQsSearchExecuted = (p: {
  keyword_count: number;
  category_count?: number;
}) => captureEvent('observations_qs_search_executed', p);

export const captureFdaCrlSearchExecuted = (p: {
  query: string;
  context: 'crl_history' | 'weekly_update';
  result_count?: number;
  is_repeat_query?: boolean;
}) => {
  if (p.query.trim().length <= 4) return;
  captureEvent('fda_crl_search_executed', p);
};

export const captureWeeklyUpdateSearched = (p: {
  query: string;
  tab: 'mhra_inspection_reports' | 'igj_toezicht' | 'recently_added';
  result_count?: number;
  is_repeat_query?: boolean;
}) => {
  if (p.query.trim().length <= 4) return;
  captureEvent('weekly_update_searched', p);
};

export const captureSupplyChainNewsSearched = (p: {
  query: string;
  result_count?: number;
  is_repeat_query?: boolean;
}) => {
  if (p.query.trim().length <= 4) return;
  captureEvent('supply_chain_news_searched', p);
};

// ---------------------------------------------------------------------------
// Page views — fired on mount for every top-level section.
// section: snake_case identifier matching the sidebar nav entry.
// sub_section: optional tab or nested view within a section.
// ---------------------------------------------------------------------------

export const capturePageViewed = (p: { section: string; sub_section?: string }) =>
  captureEvent('page_viewed', p);

// ---------------------------------------------------------------------------
// Buttons & Notifications
// ---------------------------------------------------------------------------

export const captureNotifyMeClicked = (p: {
  user_type: 'subscribed' | 'free';
  inspection_id?: string | number;
  company_name?: string;
  document_type: '483';
}) => captureEvent('notify_me_clicked', p);

export const captureContactSupportClicked = (p: {
  source: 'header' | 'login' | 'password_reset' | 'credits_expired' | 'pricing' | 'landing_pricing' | 'landing_download' | 'footer' | 'chatbot_limit' | 'freetext_limit' | 'supply_chain_limit' | 'sites_limit' | 'warning_letters_limit' | 'inspector_profile' | string;
  is_authenticated?: boolean;
}) => captureEvent('contact_support_clicked', p);

// ---------------------------------------------------------------------------
// Downloads — new types
// ---------------------------------------------------------------------------

export const captureMhraEuDownloaded = (p: {
  inspection_id?: string | number;
  company_name?: string;
  source: 'mhra_list' | 'org_detail' | 'supply_chain' | 'watchlist' | 'weekly_updates' | string;
}) => captureEvent('mhra_eu_downloaded', p);

export const captureSwissGmdpCertificateDownloaded = (p: {
  inspection_id?: string | number;
  company_name?: string;
}) => captureEvent('swiss_gmdp_certificate_downloaded', p);

export const captureOrganizationReportDownloaded = (p: {
  org_id: string | number;
  company_name?: string;
  download_type: string;
}) => captureEvent('organization_report_downloaded', p);

export const captureSiteDetailsDownloaded = (p: {
  site_id?: string | number;
  fei_number?: string;
  company_name?: string;
}) => captureEvent('site_details_downloaded', p);

export const captureCompanyProfileDownloaded = (p: {
  company_id?: string | number;
  company_name?: string;
}) => captureEvent('company_profile_downloaded', p);

// ---------------------------------------------------------------------------
// High-signal Business Events
// ---------------------------------------------------------------------------

export const captureCreditsExpiredViewed = (p: {
  feature: 'inspection_chatbot' | 'freetext_search' | 'investigator_chatbot' | 'site_detail' | 'supply_chain' | 'warning_letter' | string;
}) => captureEvent('credits_expired_viewed', p);

export const captureLandingCtaClicked = (p: {
  cta_label: 'sign_up_free' | 'start_downloading' | string;
  source_page: string;
  is_authenticated: boolean;
}) => captureEvent('landing_cta_clicked', p);

export const captureInspectorProfileCtaClicked = (p: {
  source: 'inspector_profile_buy';
  investigator_id?: string | number;
}) => captureEvent('inspector_profile_cta_clicked', p);

// ---------------------------------------------------------------------------
// Weekly Updates
// ---------------------------------------------------------------------------

export const captureWeeklyUpdateTabChanged = (p: {
  update_id?: string | number;
  tab: string;
}) => captureEvent('weekly_update_tab_changed', p);

// ---------------------------------------------------------------------------
// Onboarding & Demo Booking
// ---------------------------------------------------------------------------

export const captureOnboardingVideoSkipped = () =>
  captureEvent('onboarding_video_skipped');

export const captureOnboardingVideoCompleted = () =>
  captureEvent('onboarding_video_completed');

export const captureDemoPopupShown = (p: { show_count: number }) =>
  captureEvent('demo_popup_shown', p);

export const captureDemoPopupCtaClicked = () =>
  captureEvent('demo_popup_cta_clicked');

export const captureDemoBooked = () =>
  captureEvent('demo_booked');

export const captureSidebarWatchVideoClicked = () =>
  captureEvent('sidebar_watch_video_clicked');

export const captureSidebarBookDemoClicked = () =>
  captureEvent('sidebar_book_demo_clicked');
